import clsx from "clsx";
import { cloneDeep } from "lodash-es";
import { FC, useMemo } from "react";

import { SectionDetailCourseView } from "./SectionDetailCourseView";
import { TermsNavList } from "./TermsNavList";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import { type SectionViewOption } from "components/menus/SectionViewOptionMenu";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCourseSectionsByTerms,
  selectIsEditModeEnabled,
  termSelectors,
} from "store/selectors";
import { sortByTermYearRegex } from "store/selectors/sorting";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";

export const TermsView: FC<{ onOptionChange: (option: SectionViewOption) => void }> = ({
  onOptionChange,
}) => {
  const terms = useAppSelector(termSelectors.selectAll);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const courseSectionsByTerm = useAppSelector(selectCourseSectionsByTerms);
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorOrg } = useCapabilities();
  const canAddTerm = canAuthorOrg && isEditModeEnabled;

  const orderedTerms = useMemo(() => {
    const termsToOrder = isEditModeEnabled
      ? terms
      : terms.filter((term) => {
          const courseSections = courseSectionsByTerm[term.id];
          return courseSections.some((course) => course.sections && course.sections.length > 0);
        });

    return termsToOrder.toSorted(sortByTermYearRegex).map(cloneDeep);
  }, [terms, isEditModeEnabled, courseSectionsByTerm]);

  const currentTerm = useFindItemBySearchParamId(orderedTerms, TERM_ID_QUERY_PARAM, true);

  return (
    <div
      className={clsx(
        "flex h-full w-full items-start",
        isSmallScreen && "flex-col overflow-y-auto pb-4",
      )}
    >
      {(orderedTerms.length > 0 || canAddTerm) && (
        <TermsNavList terms={orderedTerms} onOptionChange={onOptionChange} />
      )}

      <main className={clsx("w-full", !isSmallScreen && "h-full flex-auto overflow-y-auto")}>
        {currentTerm && <SectionDetailCourseView canEdit={isEditModeEnabled} term={currentTerm} />}
      </main>
    </div>
  );
};
